import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Badge, Card, Skeleton } from '@/design-system/primitives'
import { cn } from '@/lib/cn'
import { formatMoney } from '@/lib/money'

import { useAdminMutation, useAdminQuery } from '../api'
import { BudgetEditor } from '../components/BudgetEditor'
import { ExpenseEditor } from '../components/ExpenseEditor'
import { AdminHeader } from '../AdminLayout'
import { ReceiptField } from '../components/ReceiptField'
import { CashSessionsPanel } from '../components/CashSessionsPanel'
import { RegistersPanel } from '../components/RegistersPanel'
import { SpendersPanel } from '../components/SpendersPanel'
import { useAdminContext } from '../session'
import type { Page } from '../useCollection'
import type { Budget, Expense, ExpenseCategory, FinancialSummary, I18n } from '../types'

/**
 * Le tableau de bord financier.
 *
 * Six chiffres en tête, dans l'ordre où on les lit avant une réunion de
 * clôture. Deux d'entre eux se ressemblent et ne veulent pas dire la même
 * chose — c'est la confusion la plus fréquente à la lecture d'un bilan, et
 * l'écran la lève explicitement plutôt que de laisser deviner :
 *
 *   Budget restant = prévisionnel − dépenses. Ignore les recettes.
 *   Résultat       = recettes encaissées − dépenses. Ignore le budget.
 *
 * Aucune recette ne se saisit ici. Le chiffre d'affaires vient des paiements
 * réellement constatés ; cet écran le lit, il ne l'alimente pas.
 */
export function AdminFinancePage() {
  const { t, i18n } = useTranslation()
  const { eventId, me } = useAdminContext()

  useEffect(() => {
    document.title = t('admin.section.finance')
  }, [t])

  const base = `/admin/events/${eventId}/finance`
  const label = (value: I18n) =>
    value[i18n.language] || value[Object.keys(value)[0] ?? ''] || '—'

  const summary = useAdminQuery<FinancialSummary>(
    ['admin-finance-summary', eventId, i18n.language],
    `${base}/summary?locale=${i18n.language}`,
    eventId,
  )
  const categories = useAdminQuery<Page<ExpenseCategory>>(
    ['admin-expense-categories', eventId], `${base}/expense-categories`, eventId,
  )
  const expenses = useAdminQuery<Page<Expense>>(
    ['admin-expenses', eventId], `${base}/expenses?page_size=200`, eventId,
  )
  const budget = useAdminQuery<Budget | null>(
    ['admin-budget', eventId], `${base}/budget`, eventId,
  )

  const invalidate = [
    ['admin-finance-summary', eventId, i18n.language],
    ['admin-expenses', eventId],
    ['admin-budget', eventId],
    // Le panneau « qui a dépensé » se recalcule à chaque dépense.
    ['admin-spenders', eventId],
  ]
  const saveBudget = useAdminMutation<Budget, Record<string, unknown>>(
    'put', () => `${base}/budget`, eventId, invalidate,
  )
  const create = useAdminMutation<Expense, Record<string, unknown>>(
    'post', () => `${base}/expenses`, eventId, invalidate,
  )
  const act = useAdminMutation<Expense, { id: string; action: string; reason: string | null }>(
    'post', (variables) => `${base}/expenses/${variables.id}/${variables.action}`,
    eventId, invalidate,
  )
  const save = useAdminMutation<Expense, { id: string } & Record<string, unknown>>(
    'patch', (variables) => `${base}/expenses/${variables.id}`, eventId, invalidate,
  )
  // `force` lève le garde-fou d'une dépense déjà soumise. L'annulation reste
  // préférable — même effet sur les chiffres, la ligne et sa raison
  // demeurent — mais une ligne qui ne sert vraiment à rien doit pouvoir
  // disparaître.
  const remove = useAdminMutation<{ message: string }, { id: string; force?: boolean }>(
    'delete',
    (variables) =>
      `${base}/expenses/${variables.id}${variables.force ? '?force=true' : ''}`,
    eventId,
    invalidate,
  )

  // L'identifiant plutôt que l'objet : après un enregistrement la liste est
  // rechargée, et un objet gardé en état afficherait l'ancienne version — donc
  // l'ancien montant, ce qui est exactement ce qu'on cherche à éviter ici.
  const [openExpenseId, setOpenExpenseId] = useState<string | null>(null)

  const report = summary.data
  // Le déjà-engagé par poste : planifier sous ce qui est déjà dépensé se voit
  // à la saisie, pas trois semaines plus tard sur le tableau de bord.
  const spentByCategory = new Map(
    (report?.lines ?? [])
      .filter((line) => line.category_id !== null)
      .map((line) => [line.category_id as string, line.spent_minor]),
  )
  const openExpense =
    expenses.data?.items.find((row) => row.id === openExpenseId) ?? null
  const currency = report?.currency ?? 'EUR'
  const money = (minor: number) =>
    formatMoney({ amount_minor: minor, currency }, i18n.language)

  return (
    <>
      <AdminHeader
        title={t('admin.section.finance')}
        description={t('admin.finance.description')}
      />

      {summary.isPending ? <Skeleton className="h-32 w-full" /> : null}
      {summary.isError ? (
        <p className="text-sm text-state-danger">{summary.error.message}</p>
      ) : null}

      {report ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <Figure label={t('admin.finance.budget')} value={money(report.budget_minor)} />
            <Figure label={t('admin.finance.spent')} value={money(report.spent_minor)} />
            <Figure
              label={t('admin.finance.budgetRemaining')}
              hint={t('admin.finance.budgetRemainingHint')}
              value={money(report.budget_remaining_minor)}
              tone={report.budget_remaining_minor < 0 ? 'danger' : 'neutral'}
            />
            <Figure
              label={t('admin.finance.revenue')}
              value={money(report.revenue_minor)}
              tone="champagne"
            />
            <Figure
              label={t('admin.finance.result')}
              hint={t('admin.finance.resultHint')}
              value={money(report.result_minor)}
              tone={report.result_minor < 0 ? 'danger' : 'success'}
            />
            <Figure
              label={t('admin.finance.outstanding')}
              hint={t('admin.finance.outstandingHint')}
              value={money(report.outstanding_minor)}
            />
          </div>

          <SpendersPanel eventId={eventId} />
          <RegistersPanel eventId={eventId} />
          <CashSessionsPanel eventId={eventId} />

          {report.budget_overrun_minor > 0 ? (
            <p className="mt-4 rounded-lg border border-state-danger/40 bg-state-danger/5 px-4 py-3 text-sm text-state-danger">
              {t('admin.finance.overrun', { amount: money(report.budget_overrun_minor) })}
            </p>
          ) : null}

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <Card>
              <h2 className="font-display text-heading-md text-content-primary">
                {t('admin.finance.byCategory')}
              </h2>
              {report.lines.length === 0 ? (
                <p className="mt-3 text-sm text-content-muted">{t('admin.finance.noLines')}</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {report.lines.map((line) => (
                    <li key={line.label}>
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="text-content-primary">{line.label}</span>
                        <span className="tabular-nums text-content-secondary">
                          {money(line.spent_minor)}
                          <span className="text-content-muted"> / {money(line.planned_minor)}</span>
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-bg-overlay">
                        <div
                          className={cn(
                            'h-full rounded-full',
                            line.overrun_minor > 0 ? 'bg-state-danger' : 'bg-champagne-400',
                          )}
                          style={{ width: `${Math.min(line.consumed_percent, 100)}%` }}
                        />
                      </div>
                      {line.overrun_minor > 0 ? (
                        <p className="mt-1 text-caption text-state-danger">
                          {t('admin.finance.lineOverrun', {
                            amount: money(line.overrun_minor),
                          })}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card>
              <h2 className="font-display text-heading-md text-content-primary">
                {t('admin.finance.revenueByCategory')}
              </h2>
              <p className="mt-1 text-caption text-content-muted">
                {t('admin.finance.revenueHint')}
              </p>
              {report.revenue_lines.length === 0 ? (
                <p className="mt-3 text-sm text-content-muted">{t('admin.finance.noRevenue')}</p>
              ) : (
                <ul className="mt-4 space-y-2">
                  {report.revenue_lines.map((line) => (
                    <li
                      key={line.label}
                      className="flex items-baseline justify-between gap-3 border-b border-line-subtle/50 pb-2 text-sm"
                    >
                      <span className="text-content-primary">
                        {line.label}
                        <span className="ml-2 text-caption text-content-muted">
                          {t('admin.finance.transactions', { count: line.transactions })}
                        </span>
                      </span>
                      <span className="tabular-nums text-champagne-300">
                        {money(line.gross_minor)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </>
      ) : null}

      {/* L'éditeur juste après les chiffres : on lit où on en est, puis on
          ajuste. Le proposer avant reviendrait à demander un plafond sans
          montrer ce qu'on a déjà dépensé. */}
      <BudgetEditor
        key={budget.data?.updated_at ?? 'new'}
        budget={budget.data ?? null}
        categories={categories.data?.items ?? []}
        currency={currency}
        spentByCategory={spentByCategory}
        label={label}
        money={money}
        busy={saveBudget.isPending}
        error={saveBudget.isError ? saveBudget.error.message : null}
        onSave={(payload) => saveBudget.mutate(payload)}
      />

      <h2 className="mb-4 mt-10 font-display text-heading-md text-content-primary">
        {t('admin.finance.expenses')}
      </h2>

      <ExpenseForm
        categories={categories.data?.items ?? []}
        label={label}
        busy={create.isPending}
        error={create.isError ? create.error.message : null}
        onCreate={(payload) => create.mutate(payload)}
      />

      {expenses.data ? (
        <ExpenseTable
          expenses={expenses.data.items}
          categories={categories.data?.items ?? []}
          label={label}
          money={money}
          busy={act.isPending}
          error={act.isError ? act.error.message : null}
          eventId={eventId}
          currentUserId={me?.user.id ?? null}
          onAct={(id, action, reason) => act.mutate({ id, action, reason })}
          onOpen={setOpenExpenseId}
          onChanged={() => {
            void expenses.refetch()
            void summary.refetch()
          }}
        />
      ) : null}

      {openExpense ? (
        <ExpenseEditor
          expense={openExpense}
          categories={categories.data?.items ?? []}
          label={label}
          busy={save.isPending || remove.isPending}
          error={
            save.isError
              ? save.error.message
              : remove.isError
                ? remove.error.message
                : null
          }
          onSave={(payload) =>
            save.mutate(
              { id: openExpense.id, ...payload },
              { onSuccess: () => setOpenExpenseId(null) },
            )
          }
          onDelete={() =>
            remove.mutate({ id: openExpense.id }, { onSuccess: () => setOpenExpenseId(null) })
          }
          onForceDelete={() =>
            remove.mutate(
              { id: openExpense.id, force: true },
              { onSuccess: () => setOpenExpenseId(null) },
            )
          }
          onClose={() => setOpenExpenseId(null)}
        />
      ) : null}
    </>
  )
}

function Figure({
  label,
  hint,
  value,
  tone = 'neutral',
}: {
  label: string
  hint?: string
  value: string
  tone?: 'neutral' | 'danger' | 'success' | 'champagne'
}) {
  return (
    <div className="rounded-lg border border-line-subtle bg-bg-elevated px-5 py-4">
      <p className="overline text-content-muted">{label}</p>
      <p
        className={cn(
          'mt-1 font-display text-heading-lg tabular-nums',
          tone === 'danger' && 'text-state-danger',
          tone === 'success' && 'text-state-success',
          tone === 'champagne' && 'text-champagne-300',
          tone === 'neutral' && 'text-content-primary',
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-1 text-caption text-content-muted">{hint}</p> : null}
    </div>
  )
}

const STATUS_TONES: Record<string, 'neutral' | 'ember' | 'champagne' | 'success'> = {
  draft: 'neutral',
  submitted: 'champagne',
  approved: 'success',
  paid: 'success',
  // La braise est la couleur d'alerte de la charte : le design system n'a pas
  // de ton « danger » distinct, et en inventer un ici le ferait diverger.
  rejected: 'ember',
  cancelled: 'neutral',
}

/** La dépense a-t-elle été saisie par la personne qui regarde ? */
function isOwn(expense: Expense, currentUserId: string | null): boolean {
  return currentUserId !== null && expense.created_by === currentUserId
}

/** Les gestes possibles selon l'état. Le serveur revérifie chacun d'eux. */
const ACTIONS: Record<string, string[]> = {
  draft: ['submit', 'cancel'],
  submitted: ['approve', 'reject', 'cancel'],
  approved: ['pay', 'cancel'],
  rejected: ['submit', 'cancel'],
  paid: ['cancel'],
  cancelled: [],
}

function ExpenseTable({
  expenses,
  categories,
  label,
  money,
  busy,
  error,
  eventId,
  currentUserId,
  onAct,
  onOpen,
  onChanged,
}: {
  expenses: Expense[]
  categories: ExpenseCategory[]
  label: (value: I18n) => string
  money: (minor: number) => string
  busy: boolean
  error: string | null
  eventId: string | undefined
  /** Qui regarde : on ne lui propose pas de valider sa propre dépense. */
  currentUserId: string | null
  onAct: (id: string, action: string, reason: string | null) => void
  onOpen: (id: string) => void
  /** Rejouer les listes après un ajout ou un retrait de facture. */
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const names = new Map(categories.map((row) => [row.id, label(row.name_i18n)]))

  return (
    <>
      {error ? <p className="mb-3 text-sm text-state-danger">{error}</p> : null}
      <div className="overflow-x-auto rounded-lg border border-line-subtle">
        <table className="w-full min-w-[54rem] text-sm">
          <thead>
            <tr className="border-b border-line-subtle bg-bg-elevated text-left">
              <th className="px-3 py-2 font-normal text-content-muted">{t('admin.finance.label')}</th>
              <th className="px-3 py-2 font-normal text-content-muted">{t('admin.project.category')}</th>
              <th className="px-3 py-2 text-right font-normal text-content-muted">{t('admin.finance.amount')}</th>
              <th className="px-3 py-2 font-normal text-content-muted">{t('admin.finance.date')}</th>
              <th className="px-3 py-2 font-normal text-content-muted">{t('admin.finance.status')}</th>
              <th className="px-3 py-2 font-normal text-content-muted">{t('admin.finance.receipts')}</th>
              <th className="px-3 py-2 font-normal text-content-muted">{t('admin.finance.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((expense) => (
              <tr key={expense.id} className="border-b border-line-subtle/50">
                <td className="px-3 py-2.5">
                  <span className="mr-2 font-mono text-caption text-content-muted">
                    {expense.reference}
                  </span>
                  <button
                    type="button"
                    className="text-left text-content-primary hover:text-champagne-300"
                    onClick={() => onOpen(expense.id)}
                  >
                    {expense.label}
                  </button>
                  {expense.supplier ? (
                    <span className="block text-caption text-content-muted">
                      {expense.supplier}
                    </span>
                  ) : null}

                  {/* Le motif d'un refus ou d'une annulation se lit ICI, sans
                      ouvrir quoi que ce soit : c'est la première question que
                      pose quelqu'un devant une ligne barrée. */}
                  {expense.rejected_reason ? (
                    <span className="mt-1 block text-caption text-ember-300">
                      {t('admin.finance.refusedBecause', {
                        who: expense.rejected_by_label ?? '—',
                        reason: expense.rejected_reason,
                      })}
                    </span>
                  ) : null}
                  {expense.cancel_reason ? (
                    <span className="mt-1 block text-caption text-content-muted">
                      {t('admin.finance.cancelledBecause', {
                        who: expense.cancelled_by_label ?? '—',
                        reason: expense.cancel_reason,
                      })}
                    </span>
                  ) : null}
                  {expense.approved_by_label && !expense.cancel_reason ? (
                    <span className="mt-1 block text-caption text-content-muted">
                      {t('admin.finance.approvedBy', { who: expense.approved_by_label })}
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2.5 text-content-secondary">
                  {expense.category_id ? names.get(expense.category_id) ?? '—' : '—'}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-content-primary">
                  {money(expense.amount_minor)}
                </td>
                <td className="px-3 py-2.5 text-content-secondary">
                  {new Date(expense.spent_on).toLocaleDateString()}
                </td>
                <td className="px-3 py-2.5">
                  <Badge tone={STATUS_TONES[expense.status] ?? 'neutral'}>
                    {t(`admin.finance.statuses.${expense.status}`)}
                  </Badge>
                </td>
                <td className="px-3 py-2.5">
                  <ReceiptField
                    eventId={eventId}
                    expenseId={expense.id}
                    attachments={expense.attachments ?? []}
                    onChanged={onChanged}
                  />
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onOpen(expense.id)}
                    >
                      {t('admin.finance.open')}
                    </Button>
                    {(ACTIONS[expense.status] ?? [])
                      // Valider sa propre dépense n'est pas une validation :
                      // le serveur la refuse (403), l'écran ne la propose pas.
                      .filter(
                        (action) =>
                          !(action === 'approve' && isOwn(expense, currentUserId)),
                      )
                      .map((action) => (
                      <Button
                        key={action}
                        size="sm"
                        variant={action === 'cancel' || action === 'reject' ? 'ghost' : 'outline'}
                        disabled={busy}
                        onClick={() => {
                          // Refuser ou annuler exige un motif : une écriture
                          // retirée sans raison rend le bilan invérifiable.
                          if (action === 'cancel' || action === 'reject') {
                            const reason = window.prompt(t('admin.finance.reasonPrompt'))
                            if (!reason || reason.trim().length < 3) return
                            onAct(expense.id, action, reason.trim())
                            return
                          }
                          onAct(expense.id, action, null)
                        }}
                      >
                        {t(`admin.finance.actionLabels.${action}`)}
                      </Button>
                    ))}
                  </div>
                  {expense.status === 'submitted' && isOwn(expense, currentUserId) ? (
                    <p className="mt-1 text-caption text-content-muted">
                      {t('admin.finance.selfApprovalHint')}
                    </p>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {expenses.length === 0 ? (
          <p className="px-4 py-6 text-sm text-content-muted">{t('admin.finance.noExpenses')}</p>
        ) : null}
      </div>
    </>
  )
}

function ExpenseForm({
  categories,
  label,
  busy,
  error,
  onCreate,
}: {
  categories: ExpenseCategory[]
  label: (value: I18n) => string
  busy: boolean
  error: string | null
  onCreate: (payload: Record<string, unknown>) => void
}) {
  const { t } = useTranslation()
  const today = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({
    label: '',
    amount: '',
    category_id: '',
    spent_on: today,
    supplier: '',
  })

  const field =
    'rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary'

  return (
    <form
      className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-6"
      onSubmit={(event) => {
        event.preventDefault()
        const amount = Number.parseFloat(form.amount.replace(',', '.'))
        if (!form.label.trim() || !Number.isFinite(amount) || amount <= 0) return
        onCreate({
          label: form.label.trim(),
          // Le serveur ne manipule que des unités mineures : un centime perdu
          // dans un flottant se retrouve dans le bénéfice.
          amount_minor: Math.round(amount * 100),
          category_id: form.category_id || null,
          spent_on: form.spent_on,
          supplier: form.supplier.trim() || null,
        })
        setForm({ label: '', amount: '', category_id: '', spent_on: today, supplier: '' })
      }}
    >
      <input
        className={cn(field, 'lg:col-span-2')}
        placeholder={t('admin.finance.label')}
        value={form.label}
        onChange={(event) => setForm({ ...form, label: event.target.value })}
      />
      <input
        className={field}
        inputMode="decimal"
        placeholder={t('admin.finance.amount')}
        value={form.amount}
        onChange={(event) => setForm({ ...form, amount: event.target.value })}
      />
      <select
        className={field}
        value={form.category_id}
        onChange={(event) => setForm({ ...form, category_id: event.target.value })}
      >
        <option value="">{t('admin.finance.noCategory')}</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {label(category.name_i18n)}
          </option>
        ))}
      </select>
      <input
        className={field}
        type="date"
        value={form.spent_on}
        onChange={(event) => setForm({ ...form, spent_on: event.target.value })}
      />
      <Button type="submit" disabled={busy}>
        {t('admin.finance.addExpense')}
      </Button>

      {error ? (
        <p className="text-sm text-state-danger lg:col-span-6">{error}</p>
      ) : null}
    </form>
  )
}
